import { API_URLS, getApiConfig, type ApiConfig } from "./api";

type Environment = ApiConfig["environment"];

// Fired on window when the session can no longer be renewed; AuthContext
// listens for it and logs the user out so ProtectedRoute redirects to /login.
export const AUTH_EXPIRED_EVENT = "auth:expired";

// DEV and LIVE issue different tokens, so each environment keeps its own pair.
const accessTokenKey = (env: Environment) => `authToken:${env}`;
const refreshTokenKey = (env: Environment) => `refreshToken:${env}`;

const currentEnvironment = (): Environment => getApiConfig().environment;

const USER_STORAGE_KEYS = [
  "isAuthenticated",
  "userEmail",
  "userId",
  "userName",
  "loginTime",
];

// Keys from before tokens were stored per environment.
const LEGACY_TOKEN_KEYS = ["authToken", "refreshToken"];

export const getAccessToken = (
  env: Environment = currentEnvironment()
): string | null => localStorage.getItem(accessTokenKey(env));

export const saveSessionTokens = (
  accessToken: string,
  refreshToken: string,
  env: Environment = currentEnvironment()
): void => {
  localStorage.setItem(accessTokenKey(env), accessToken);
  localStorage.setItem(refreshTokenKey(env), refreshToken);
};

// Full logout: forget the user and the tokens for every environment.
export const clearAuthStorage = (): void => {
  const envs = Object.keys(API_URLS) as Environment[];
  [
    ...USER_STORAGE_KEYS,
    ...LEGACY_TOKEN_KEYS,
    ...envs.flatMap((env) => [accessTokenKey(env), refreshTokenKey(env)]),
  ].forEach((key) => localStorage.removeItem(key));
};

// Send the user to login for this environment, keeping the other
// environment's tokens so switching back doesn't need a fresh login.
export const expireSession = (env: Environment = currentEnvironment()): void => {
  localStorage.removeItem(accessTokenKey(env));
  localStorage.removeItem(refreshTokenKey(env));
  localStorage.removeItem("isAuthenticated");
  globalThis.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

// Shared across concurrent 401s so each refresh token is only spent once.
const renewInFlight: Partial<Record<Environment, Promise<string | null>>> = {};

const requestNewAccessToken = async (
  env: Environment
): Promise<string | null> => {
  const refreshToken = localStorage.getItem(refreshTokenKey(env));
  if (!refreshToken) return null;

  const response = await fetch(`${API_URLS[env]}/api/auth/v1.0.0/token/renew`, {
    method: "POST",
    headers: { accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) return null;

  const result = await response.json();
  const accessToken: string | undefined = result?.data?.accessToken;
  if (result?.status !== "success" || !accessToken) return null;

  localStorage.setItem(accessTokenKey(env), accessToken);
  return accessToken;
};

const renewAccessToken = (env: Environment): Promise<string | null> => {
  const pending =
    renewInFlight[env] ??
    requestNewAccessToken(env).finally(() => {
      delete renewInFlight[env];
    });
  renewInFlight[env] = pending;
  return pending;
};

const withAuthHeader = (init: RequestInit, token: string | null): RequestInit => {
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
};

// fetch() for authenticated endpoints: attaches the access token for the
// current API environment and, on 401, renews it once with the refresh token
// and retries. If renewal is rejected the session for that environment is
// expired and AUTH_EXPIRED_EVENT is fired.
export const authFetch = async (
  input: string,
  init: RequestInit = {}
): Promise<Response> => {
  const env = currentEnvironment();
  const response = await fetch(input, withAuthHeader(init, getAccessToken(env)));
  if (response.status !== 401) return response;

  let newToken: string | null;
  try {
    newToken = await renewAccessToken(env);
  } catch (error) {
    // Network failure while renewing — keep the session, surface the 401.
    console.error("Error renewing access token:", error);
    return response;
  }

  if (!newToken) {
    expireSession(env);
    return response;
  }

  return fetch(input, withAuthHeader(init, newToken));
};
